import React, { FormEvent, useState } from 'react'
import { useMutation } from 'react-query'
import { AxiosError } from 'axios'
import styled from 'styled-components'

import { Helmet } from 'react-helmet'
import { Link, NavLinkProps, useHistory, useLocation } from 'react-router-dom'

import {
  Box,
  FormControl,
  Input,
  InputLabel,
  Button as MuiButton,
  Paper,
  Typography,
} from '@material-ui/core'
import MuiAlert from '@material-ui/lab/Alert'
import { spacing } from '@material-ui/system'
import { skuit } from 'src/services/skuit'
import { KioskScope } from 'src/services/skuit/Auth'
import { StagingBadge } from 'src/components/StagingBadge'

const Button = styled<any>(MuiButton)(spacing)
const Alert = styled(MuiAlert)(spacing)

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)}px;

  ${(props) => props.theme.breakpoints.up('md')} {
    padding: ${(props) => props.theme.spacing(10)}px;
  }
`

const NavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>(
  ({ to, ...props }, ref) => {
    if (typeof to === "string" && to.startsWith("/v2")) {
      // Render a plain <a>
      return <a ref={ref} href={to} {...props} />
    }

    // Render a React Router NavLink
    return <Link innerRef={ref} to={to} {...props} />
  }
)
NavLink.displayName = 'NavLink'

const DEFAULT_REDIRECT_CLIENT = '/v2/dashboard'
const DEFAULT_REDIRECT_OTHER = '/v2/dashboards/orders-shipments'

const getDefaultRedirect = () => {
  return skuit.auth.getAccountType() === 'client' ? DEFAULT_REDIRECT_CLIENT : DEFAULT_REDIRECT_OTHER
}

const doRedirect = (url: string, history: ReturnType<typeof useHistory>) => {
  if (url.startsWith('http') || url.startsWith('/v2')) {
    window.location.assign(url)
  } else {
    history.push(url)
  }
}

export const Login = () => {
  const history = useHistory()
  const location = useLocation()
  const redirectParam = new URLSearchParams(location.search).get('redirect')
  const [error, setError] = useState('')

  const {isLoading, mutate: login} = useMutation(skuit.auth.login, {
    onSuccess: () => {
      doRedirect(redirectParam || getDefaultRedirect(), history)
    },
    onError: (err: AxiosError) => {
      if (err.response && err.response.status === 401) {
        setError('Username and password do not match!')
      } else {
        setError(err.toString())
      }
    },
  })

  if (skuit.auth.isAuthed()) {
    doRedirect(redirectParam || getDefaultRedirect(), history)
    return null
  }

  const showKiosk = skuit.auth.kioskAuth && skuit.auth.kioskAuth.expiresOn > new Date() && skuit.auth.kioskAuth.scopes.includes(KioskScope.DriverCheckIn)

  const submitForm = (e: FormEvent) => {
    e.preventDefault()
    const data = new FormData(e.target as HTMLFormElement)
    login({
      username: data.get('email') as string,
      password: data.get('password') as string,
    })
  }

  return (
    <Wrapper>
      <Helmet title="Sign In" />

      <Typography component="h1" variant="h4" align="center" gutterBottom>
        Welcome Back!
      </Typography>
      <Typography component="h2" variant="body1" align="center">
        Sign in to your account to continue
      </Typography>
      <Box mt={2} textAlign="center">
        <StagingBadge />
      </Box>
      <form onSubmit={submitForm}>
        {error && <Box mt={2}><Alert severity="error">{error}</Alert></Box>}
        <FormControl margin="normal" required fullWidth error={!!error}>
          <InputLabel htmlFor="email">Email Address</InputLabel>
          <Input id="email" name="email" autoComplete="email" autoFocus />
        </FormControl>
        <FormControl margin="normal" required fullWidth error={!!error}>
          <InputLabel htmlFor="password">Password</InputLabel>
          <Input name="password" type="password" id="password" autoComplete="current-password" />
        </FormControl>
        <Button
          type="submit"
          disabled={isLoading}
          fullWidth
          variant="contained"
          color="primary"
          mb={2}
        >
          Sign in
        </Button>
        <Button component={Link} to="/auth/forgot-password" fullWidth color="primary">
          Forgot password
        </Button>
          {
            showKiosk && (
              <Button component={NavLink} to="/v2/kiosk/driver-check-in" fullWidth color="primary">Driver Check-In Kiosk</Button>
            )
          }
      </form>
    </Wrapper>
  )
}
